import React, { useState } from "react";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import Button from "components/ui/Button";

const subjectProperty = {
  id: "sub-001",
  address: "5412 Farnam Street",
  city: "Omaha",
  state: "NE",
  zip: "68132",
  price: 385000,
  beds: 4,
  baths: 3,
  sqft: 2480,
  lotSize: 0.22,
  yearBuilt: 2008,
  propertyType: "Single Family",
  status: "Active",
  daysOnMarket: 12,
  pricePerSqft: 155,
  condition: "Excellent",
  garage: 2,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f5e1cc94-1772578640542.png",
  imageAlt: "Modern two-story single family home with white exterior, manicured lawn, and attached two-car garage in Omaha Midtown neighborhood",
  avmLow: 372000,
  avmMid: 389000,
  avmHigh: 406000,
  avmConfidence: 87,
  mlsId: "MLS-2026-04821",
  features: ["Hardwood Floors", "Updated Kitchen", "Central AC", "Finished Basement", "Deck"]
};

export default function SubjectPropertyCard({ onEdit }) {
  const [expanded, setExpanded] = useState(false);

  const fmt = (n) => n?.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  return (
    <div className="card-base flex flex-col gap-0 overflow-hidden p-0">
      {/* Header */}
      <div className="bg-primary px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="Home" size={15} color="white" />
          <span className="text-xs font-caption font-semibold text-white uppercase tracking-wide">Subject Property</span>
        </div>
        <span className="text-xs font-caption bg-white/20 text-white px-2 py-0.5 rounded-full">{subjectProperty?.mlsId}</span>
      </div>
      {/* Image */}
      <div className="relative w-full h-44 overflow-hidden">
        <Image
          src={subjectProperty?.image}
          alt={subjectProperty?.imageAlt}
          className="w-full h-full object-cover" />
        
        <div className="absolute top-2 right-2 bg-secondary text-white text-xs font-caption font-semibold px-2 py-1 rounded-full">
          {subjectProperty?.status}
        </div>
        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs font-caption px-2 py-1 rounded">
          DOM: {subjectProperty?.daysOnMarket} days
        </div>
      </div>
      {/* Core Info */}
      <div className="p-4 flex flex-col gap-3">
        <div>
          <h3 className="font-heading font-semibold text-base text-foreground leading-tight">{subjectProperty?.address}</h3>
          <p className="text-sm text-muted-foreground font-caption">{subjectProperty?.city}, {subjectProperty?.state} {subjectProperty?.zip}</p>
        </div>

        <div className="text-2xl font-heading font-bold text-primary">{fmt(subjectProperty?.price)}</div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-2 text-center">
          {[
          { icon: "BedDouble", val: subjectProperty?.beds, label: "Beds" },
          { icon: "Bath", val: subjectProperty?.baths, label: "Baths" },
          { icon: "Maximize2", val: subjectProperty?.sqft?.toLocaleString(), label: "Sq Ft" }]?.
          map((s) =>
          <div key={s?.label} className="bg-muted rounded-lg py-2 px-1">
              <Icon name={s?.icon} size={14} color="var(--color-primary)" className="mx-auto mb-0.5" />
              <div className="font-mono-data text-sm font-semibold text-foreground">{s?.val}</div>
              <div className="text-xs text-muted-foreground font-caption">{s?.label}</div>
            </div>
          )}
        </div>

        {/* Price/sqft */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground font-caption">Price/Sq Ft</span>
          <span className="font-mono-data font-semibold text-foreground">${subjectProperty?.pricePerSqft}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground font-caption">Year Built</span>
          <span className="font-mono-data font-semibold text-foreground">{subjectProperty?.yearBuilt}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground font-caption">Lot Size</span>
          <span className="font-mono-data font-semibold text-foreground">{subjectProperty?.lotSize} ac</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground font-caption">Condition</span>
          <span className="text-xs font-caption font-semibold text-secondary bg-secondary/10 px-2 py-0.5 rounded-full">{subjectProperty?.condition}</span>
        </div>

        {/* AVM Section */}
        <div className="border border-border rounded-lg p-3 bg-muted/40">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-caption font-semibold text-foreground uppercase tracking-wide">AVM Estimate</span>
            <span className="text-xs font-caption text-secondary font-semibold">{subjectProperty?.avmConfidence}% confidence</span>
          </div>
          <div className="flex items-center justify-between gap-1">
            <div className="text-center flex-1">
              <div className="text-xs text-muted-foreground font-caption">Low</div>
              <div className="font-mono-data text-xs font-semibold text-foreground">{fmt(subjectProperty?.avmLow)}</div>
            </div>
            <div className="text-center flex-1 border-x border-border">
              <div className="text-xs text-muted-foreground font-caption">Mid</div>
              <div className="font-mono-data text-sm font-bold text-primary">{fmt(subjectProperty?.avmMid)}</div>
            </div>
            <div className="text-center flex-1">
              <div className="text-xs text-muted-foreground font-caption">High</div>
              <div className="font-mono-data text-xs font-semibold text-foreground">{fmt(subjectProperty?.avmHigh)}</div>
            </div>
          </div>
          {/* Confidence bar */}
          <div className="mt-2 h-1.5 bg-border rounded-full overflow-hidden">
            <div className="h-full bg-secondary rounded-full" style={{ width: `${subjectProperty?.avmConfidence}%` }} />
          </div>
        </div>

        {/* Features toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs font-caption text-primary hover:underline transition-standard">
          
          <Icon name={expanded ? "ChevronUp" : "ChevronDown"} size={13} color="currentColor" />
          {expanded ? "Hide" : "Show"} Features ({subjectProperty?.features?.length})
        </button>

        {expanded &&
        <div className="flex flex-wrap gap-1.5">
            {subjectProperty?.features?.map((f) =>
          <span key={f} className="text-xs font-caption bg-primary/10 text-primary px-2 py-0.5 rounded-full">{f}</span>
          )}
          </div>
        }

        <Button variant="outline" size="sm" iconName="Edit2" iconPosition="left" iconSize={13} onClick={onEdit} fullWidth>
          Edit Subject Property
        </Button>
      </div>
    </div>);

}